"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import BottomCta from "@/components/BottomCta";
import { Check, CheckCircle2, Zap, DollarSign, Shield, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PACKAGES = [
  { icon: DollarSign, title: "Transparent Pricing", desc: "Clear, itemized costs with no hidden fees or surprise charges." },
  { icon: Shield, title: "Fixed Service Fees", desc: "Predictable pricing based on your business activity and setup requirements." },
  { icon: Zap, title: "Flexible Payment Plans", desc: "Multiple payment options tailored to your startup's financial planning." },
  { icon: TrendingUp, title: "Value-Driven Solutions", desc: "Comprehensive packages designed to maximize your investment returns." },
  { icon: Check, title: "100% Compliance", desc: "Every package ensures full adherence to latest UAE regulations." },
  { icon: CheckCircle2, title: "No Surprises", desc: "Complete transparency with detailed breakdowns of all government costs." },
];

const STEPS = [
  { num: "01", title: "Define Your Needs", desc: "Tell us your business activity, jurisdiction preference, and visa requirements." },
  { num: "02", title: "Receive Custom Quote", desc: "We provide a detailed, itemized breakdown of all costs and services." },
  { num: "03", title: "Choose Your Package", desc: "Select the plan that best fits your budget and business objectives." },
  { num: "04", title: "Setup & Implementation", desc: "We handle everything from licensing to visa processing with transparency." },
];

const WHY_CHOOSE = [
  "Completely transparent cost breakdowns",
  "No hidden government or service charges",
  "Flexible payment plans for startups",
  "Expert guidance to optimize your investment",
  "Fixed pricing with guaranteed compliance",
];

export default function PricingPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
    <Navbar />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Transparent Pricing
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Business Setup Pricing <br />
            <span className="font-serif font-normal text-[#B8893A]">Without Hidden Costs</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            A clear, transparent view of how business setup in Dubai and UAE free zones is priced. Our packages are designed to give you a strong foundation with complete cost visibility and no surprises.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link
              href="/calculator"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Calculate Your Cost
            </Link>
            <Link
              href="/contact"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHY PRICING MATTERS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Transparent Pricing Matters</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Know your investment from day one</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Clear pricing helps you plan your startup budget with confidence. No hidden fees, no surprise charges — just straightforward costs that reflect the actual value you receive.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">What You Get</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Complete transparency and value</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Fixed service fees with detailed government cost breakdowns. Flexible payment options, expert guidance, and compliance assurance — everything tailored to your business setup journey.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Pricing Advantage</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Complete transparency with flexible solutions</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {PACKAGES.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple. Clear. Efficient.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-8 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PACKAGES */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Complete Pricing Packages</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">All-inclusive solutions for your business setup</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="mb-12">
            <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8">
              Our packages include company formation, trade licenses, visa processing, bank account opening, and compliance support. Pricing varies based on your jurisdiction, business activity, and visa requirements. Use our calculator or contact us for a personalized quote.
            </p>
            <Link
              href="/calculator"
              className="block w-fit mx-auto px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Get Your Instant Quote
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Transparent pricing you can trust</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              We believe in clear communication and honest pricing. No hidden costs, no surprise fees — just straightforward solutions designed to help your business succeed from day one.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-[#FAFAFA] rounded-xl">
                <Check className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Ready to Get Started?"
        subtitle="Get an instant quote with our smart calculator, or speak with an expert for a personalized business setup plan."
      />

      <Footer />
    </main>
  );
}
